import { useEffect, useId, useRef, useState } from 'react';
import { MAX_CHAT_LENGTH } from '../../../shared/protocol.ts';
import type { ChatMessageFrame } from '../../../shared/protocol.ts';

export interface ChatPanelProps {
  readonly messages: readonly ChatMessageFrame[];
  readonly yourUserId: string | null;
  readonly onSend: (text: string) => void;
  readonly disabled: boolean;
  /** Defaults suit a two-player match; the waiting room is a room, not a duel. */
  readonly heading?: string;
  readonly emptyHint?: string;
  readonly inputLabel?: string;
}

/**
 * Identifies a line.
 *
 * `chatSeq` restarts per scope, so a lobby's third message and its match's
 * third message collide unless the scope is part of the key.
 */
function keyOf(message: ChatMessageFrame): string {
  return message.scope.kind === 'match'
    ? `match:${message.scope.matchId}:${message.chatSeq}`
    : `lobby:${message.scope.code}:${message.chatSeq}`;
}

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Match chat.
 *
 * The log is a `<ol>` inside an `aria-live="polite"` region so a screen reader
 * hears new lines without losing the list semantics. It is deliberately the
 * *second* live region in the app — the first narrates the game — and it is
 * scoped tightly here so the two never describe the same event.
 */
export function ChatPanel({
  messages,
  yourUserId,
  onSend,
  disabled,
  heading = 'Chat',
  emptyHint = 'Say hello to your opponent.',
  inputLabel = 'Message your opponent',
}: ChatPanelProps) {
  const [draft, setDraft] = useState('');
  const logRef = useRef<HTMLOListElement | null>(null);
  const inputId = useId();

  // Follow the conversation, but only when the reader is already at the bottom:
  // yanking the scroll position while someone reads back is worse than a
  // missed message they can scroll to.
  useEffect(() => {
    const log = logRef.current;
    if (!log) return;
    const nearBottom = log.scrollHeight - log.scrollTop - log.clientHeight < 80;
    if (nearBottom) log.scrollTop = log.scrollHeight;
  }, [messages]);

  const send = (event: React.FormEvent): void => {
    event.preventDefault();
    const text = draft.trim();
    if (!text || disabled) return;
    onSend(text);
    setDraft('');
  };

  const remaining = MAX_CHAT_LENGTH - draft.length;

  return (
    <section className="chat" aria-labelledby={`${inputId}-heading`}>
      <h3 className="chat__heading" id={`${inputId}-heading`}>
        {heading}
      </h3>

      <ol className="chat__log" ref={logRef} aria-live="polite" aria-relevant="additions">
        {messages.length === 0 && <li className="chat__empty">{emptyHint}</li>}
        {messages.map((message) => {
          const mine = message.from?.userId === yourUserId;
          return (
            <li
              key={keyOf(message)}
              className={[
                'chat__line',
                message.kind === 'system' ? 'chat__line--system' : `chat__line--seat${message.from?.seat ?? 0}`,
                mine ? 'chat__line--mine' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {message.kind === 'system' ? (
                <span className="chat__system">{message.text}</span>
              ) : (
                <>
                  <span className="chat__author">
                    {message.from?.displayName}
                    {mine && <span className="visually-hidden"> (you)</span>}
                  </span>
                  <span className="chat__text">{message.text}</span>
                  <time className="chat__time" dateTime={message.at}>
                    {timeOf(message.at)}
                  </time>
                </>
              )}
            </li>
          );
        })}
      </ol>

      <form className="chat__form" onSubmit={send}>
        <label className="visually-hidden" htmlFor={inputId}>
          {inputLabel}
        </label>
        <input
          id={inputId}
          className="chat__input"
          value={draft}
          onChange={(event) => setDraft(event.target.value.slice(0, MAX_CHAT_LENGTH))}
          placeholder={disabled ? 'Chat is unavailable' : 'Say something'}
          maxLength={MAX_CHAT_LENGTH}
          disabled={disabled}
          autoComplete="off"
          enterKeyHint="send"
        />
        <button type="submit" className="button button--quiet" disabled={disabled || !draft.trim()}>
          Send
        </button>
      </form>
      {/* Only warn near the limit; a permanent counter is noise. */}
      {remaining <= 30 && (
        <p className="chat__remaining" aria-live="polite">
          {remaining} characters left
        </p>
      )}
    </section>
  );
}
